import { useEffect } from 'react';
import { useMe } from '@/features/auth/api';
import { useTheme } from '@/theme/ThemeProvider';
import { applyLanguagePreference, getLanguagePreference } from '@/i18n';

/**
 * Pure subscriber. Mount under <App /> only while a user is logged in:
 * watches `useMe().data.preferences` and applies non-null overrides to the
 * local ThemeProvider and i18next.
 *
 * It does NOT write to the server — that path is owned by SettingsPage
 * via the useUpdatePreferences mutation. When that mutation succeeds the
 * Me cache updates, this effect re-runs, and the local state catches up.
 * When the mutation fails the Me cache is unchanged and the next render
 * (or page load) rolls the UI back to the server's value.
 */
export function ServerPrefsSync(): null {
  const me = useMe();
  const { theme, setTheme } = useTheme();

  const serverTheme = me.data?.preferences?.theme ?? null;
  const serverLang = me.data?.preferences?.language ?? null;

  useEffect(() => {
    if (!serverTheme) return;
    if (serverTheme !== theme) setTheme(serverTheme);
  }, [serverTheme, theme, setTheme]);

  useEffect(() => {
    if (!serverLang) return;
    if (serverLang !== getLanguagePreference()) {
      void applyLanguagePreference(serverLang);
    }
  }, [serverLang]);

  return null;
}
